// =============================================================================
// GOOGLE ANALYTICS SERVICE
// =============================================================================
// Comprehensive analytics tracking service for user behavior and conversions

interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class AnalyticsService {
  private isEnabled: boolean;

  constructor() {
    this.isEnabled = this.checkIfEnabled();
  }

  /**
   * Check if analytics is enabled (production only)
   */
  private checkIfEnabled(): boolean {
    const isProd = import.meta.env.PROD;
    const hasGtag = typeof window !== 'undefined' && typeof window.gtag === 'function';
    return isProd && hasGtag;
  }

  /**
   * Track page views
   */
  public trackPageView(pagePath: string, pageTitle: string): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'page_view', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: pagePath,
    });
  }

  /**
   * Track custom events
   */
  public trackEvent(event: GAEvent): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    });
  }

  // =============================================================================
  // USER ENGAGEMENT EVENTS
  // =============================================================================

  /**
   * Track button clicks
   */
  public trackButtonClick(buttonName: string, location: string): void {
    this.trackEvent({
      action: 'button_click',
      category: 'engagement',
      label: `${buttonName} - ${location}`,
    });
  }

  /**
   * Track link clicks
   */
  public trackLinkClick(linkText: string, destination: string): void {
    this.trackEvent({
      action: 'link_click',
      category: 'engagement',
      label: `${linkText} -> ${destination}`,
    });
  }

  /**
   * Track scroll depth
   */
  public trackScrollDepth(percentage: number, pagePath: string): void {
    this.trackEvent({
      action: 'scroll_depth',
      category: 'engagement',
      label: pagePath,
      value: percentage,
    });
  }

  /**
   * Track time on page
   */
  public trackTimeOnPage(seconds: number, pagePath: string): void {
    this.trackEvent({
      action: 'time_on_page',
      category: 'engagement',
      label: pagePath,
      value: Math.round(seconds),
    });
  }

  /**
   * Track outbound link clicks
   */
  public trackOutboundLink(destination: string, linkText?: string): void {
    this.trackEvent({
      action: 'outbound_link',
      category: 'engagement',
      label: linkText ? `${linkText} -> ${destination}` : destination,
    });
  }

  // =============================================================================
  // FORM INTERACTION EVENTS
  // =============================================================================

  /**
   * Track form starts
   */
  public trackFormStart(formName: string): void {
    this.trackEvent({
      action: 'form_start',
      category: 'forms',
      label: formName,
    });
  }

  /**
   * Track form submissions
   */
  public trackFormSubmission(formName: string, success: boolean): void {
    this.trackEvent({
      action: success ? 'form_submit_success' : 'form_submit_error',
      category: 'forms',
      label: formName,
    });
  }

  /**
   * Track form field interactions
   */
  public trackFormFieldInteraction(formName: string, fieldName: string): void {
    this.trackEvent({
      action: 'form_field_interaction',
      category: 'forms',
      label: `${formName} - ${fieldName}`,
    });
  }

  /**
   * Track form abandonment
   */
  public trackFormAbandonment(formName: string, lastField: string): void {
    this.trackEvent({
      action: 'form_abandonment',
      category: 'forms',
      label: `${formName} - ${lastField}`,
    });
  }

  // =============================================================================
  // CONVERSION EVENTS
  // =============================================================================

  /**
   * Track contact form submissions
   */
  public trackContactFormSubmit(success: boolean): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'generate_lead', {
      event_category: 'conversion',
      event_label: 'contact_form',
      value: success ? 1 : 0,
    });
  }

  /**
   * Track appointment bookings
   */
  public trackAppointmentBooking(serviceType: string, success: boolean): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'schedule_appointment', {
      event_category: 'conversion',
      event_label: serviceType,
      value: success ? 1 : 0,
    });
  }

  /**
   * Track newsletter signups
   */
  public trackNewsletterSignup(location: string, success: boolean): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'sign_up', {
      event_category: 'conversion',
      event_label: `newsletter - ${location}`,
      method: 'email',
      value: success ? 1 : 0,
    });
  }

  /**
   * Track payment initiation
   */
  public trackPaymentInitiation(amount: number, serviceType: string): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'begin_checkout', {
      event_category: 'ecommerce',
      currency: 'USD',
      value: amount,
      items: [
        {
          item_id: serviceType,
          item_name: serviceType,
          price: amount,
        },
      ],
    });
  }

  /**
   * Track payment completion
   */
  public trackPaymentCompletion(
    transactionId: string,
    amount: number,
    serviceType: string
  ): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'purchase', {
      transaction_id: transactionId,
      value: amount,
      currency: 'USD',
      items: [
        {
          item_id: serviceType,
          item_name: serviceType,
          price: amount,
        },
      ],
    });
  }

  // =============================================================================
  // SERVICE & CONTENT EVENTS
  // =============================================================================

  /**
   * Track service page views
   */
  public trackServiceView(serviceName: string): void {
    this.trackEvent({
      action: 'service_view',
      category: 'services',
      label: serviceName,
    });
  }

  /**
   * Track service inquiries
   */
  public trackServiceInquiry(serviceName: string): void {
    this.trackEvent({
      action: 'service_inquiry',
      category: 'services',
      label: serviceName,
    });
  }

  /**
   * Track content interactions
   */
  public trackContentInteraction(contentType: string, contentName: string, action: string): void {
    this.trackEvent({
      action: 'content_interaction',
      category: 'content',
      label: `${contentType} - ${contentName} - ${action}`,
    });
  }

  /**
   * Track FAQ interactions
   */
  public trackFAQInteraction(question: string, action: 'open' | 'close'): void {
    this.trackEvent({
      action: 'faq_interaction',
      category: 'content',
      label: `${action} - ${question}`,
    });
  }

  /**
   * Track testimonial views
   */
  public trackTestimonialView(testimonialId: string): void {
    this.trackEvent({
      action: 'testimonial_view',
      category: 'content',
      label: testimonialId,
    });
  }

  // =============================================================================
  // NAVIGATION EVENTS
  // =============================================================================

  /**
   * Track navigation menu interactions
   */
  public trackNavigationClick(destination: string, source: string = 'navbar'): void {
    this.trackEvent({
      action: 'navigation_click',
      category: 'navigation',
      label: `${source} -> ${destination}`,
    });
  }

  /**
   * Track mobile menu toggle
   */
  public trackMobileMenuToggle(action: 'open' | 'close'): void {
    this.trackEvent({
      action: 'mobile_menu_toggle',
      category: 'navigation',
      label: action,
    });
  }

  /**
   * Track CTA button clicks
   */
  public trackCTAClick(ctaText: string, location: string, destination: string): void {
    this.trackEvent({
      action: 'cta_click',
      category: 'navigation',
      label: `${ctaText} - ${location} -> ${destination}`,
    });
  }

  // =============================================================================
  // MEDIA & ASSET EVENTS
  // =============================================================================

  /**
   * Track file downloads
   */
  public trackFileDownload(fileName: string, fileType: string): void {
    this.trackEvent({
      action: 'file_download',
      category: 'media',
      label: `${fileName} (${fileType})`,
    });
  }

  /**
   * Track image gallery interactions
   */
  public trackGalleryInteraction(action: string, imageId?: string): void {
    this.trackEvent({
      action: 'gallery_interaction',
      category: 'media',
      label: imageId ? `${action} - ${imageId}` : action,
    });
  }

  /**
   * Track video interactions
   */
  public trackVideoInteraction(videoName: string, action: 'play' | 'pause' | 'complete'): void {
    this.trackEvent({
      action: 'video_interaction',
      category: 'media',
      label: `${action} - ${videoName}`,
    });
  }

  // =============================================================================
  // ERROR & PERFORMANCE TRACKING
  // =============================================================================

  /**
   * Track errors
   */
  public trackError(errorMessage: string, errorLocation: string, fatal: boolean = false): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'exception', {
      description: `${errorLocation}: ${errorMessage}`,
      fatal,
    });
  }

  /**
   * Track 404 errors
   */
  public track404Error(path: string): void {
    this.trackEvent({
      action: '404_error',
      category: 'errors',
      label: path,
    });
  }

  /**
   * Track search queries (if search is implemented)
   */
  public trackSearch(searchTerm: string, resultCount: number): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('event', 'search', {
      search_term: searchTerm,
      event_category: 'engagement',
      value: resultCount,
    });
  }

  // =============================================================================
  // USER PROPERTIES
  // =============================================================================

  /**
   * Set user properties
   */
  public setUserProperty(propertyName: string, value: string): void {
    if (!this.isEnabled) {
      return;
    }

    window.gtag?.('set', 'user_properties', {
      [propertyName]: value,
    });
  }

  /**
   * Track user preferences
   */
  public trackUserPreference(preferenceName: string, value: string): void {
    this.setUserProperty(preferenceName, value);
    this.trackEvent({
      action: 'preference_change',
      category: 'user',
      label: `${preferenceName}: ${value}`,
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

